import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Icon, Label, Popup } from 'semantic-ui-react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStockInconsistencySummary } from '../context/StockInconsistencySummaryContext';
import { useOrderSummary } from '../context/OrderSummaryContext';
import StockInconsistencyBanner from '../misc/StockInconsistencyBanner';
import PaymentNoticeBanner from '../payments/PaymentNoticeBanner';
import { useSyncControls } from '../../hooks/useSyncControls';
import { usePaymentStatus } from '../../hooks/usePaymentStatus';
import {
  getVisibleNavigation,
  isPathMatch,
  resolveActiveNavKey,
  resolvePageMeta,
} from './appNavigation';
import './appShell.css';

const SIDEBAR_STORAGE_KEY = 'stocky.appShell.sidebarCollapsed';
const desktopMediaQuery = '(min-width: 1024px)';

function SidebarNavItem({
  item,
  active,
  collapsed,
  badge,
  onNavigate,
  showChildren,
  pathname,
}) {
  const badgeValue = Number.isFinite(Number(badge)) ? Number(badge) : 0;
  const showBadge = badgeValue > 0;
  const itemContent = (
    <Link
      to={item.to}
      className={`app-shell__nav-item${active ? ' is-active' : ''}${collapsed ? ' is-collapsed' : ''}`}
      aria-current={active ? 'page' : undefined}
      aria-label={item.label}
      onClick={onNavigate}
    >
      <span className="app-shell__nav-icon">
        <Icon name={item.icon} style={{ margin: 0 }} />
        {collapsed && showBadge ? <span className="app-shell__badge app-shell__badge--floating">{badgeValue}</span> : null}
      </span>
      {!collapsed ? (
        <>
          <span className="app-shell__nav-label">{item.label}</span>
          {showBadge ? <Label color="red" size="tiny" circular>{badgeValue}</Label> : null}
        </>
      ) : null}
    </Link>
  );

  return (
    <div>
      {collapsed ? (
        <Popup
          content={item.label}
          position="right center"
          trigger={itemContent}
        />
      ) : itemContent}
      {!collapsed && showChildren && Array.isArray(item.children) && item.children.length > 0 ? (
        <div className="app-shell__subnav">
          {item.children.map((child) => {
            const childActive = isPathMatch(pathname, child.matchers || [child.to]);
            return (
              <Link
                key={child.key}
                to={child.to}
                className={`app-shell__subnav-item${childActive ? ' is-active' : ''}`}
                aria-current={childActive ? 'page' : undefined}
                onClick={onNavigate}
              >
                {child.label}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getUser, isAdmin, userLogout } = useAuth();
  const { summary } = useStockInconsistencySummary();
  const { summary: orderSummary } = useOrderSummary();
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(desktopMediaQuery).matches : true,
  );
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentUser = getUser();
  const authUser = useMemo(
    () => (currentUser?.accessToken ? { accessToken: currentUser.accessToken } : null),
    [currentUser?.accessToken],
  );
  const currentUsername =
    currentUser?.data?.username ||
    currentUser?.data?.name ||
    currentUser?.data?.email ||
    'Usuario';
  const currentUserEmail = currentUser?.data?.email || currentUser?.data?.username || 'Sin email';
  const userIsAdmin = isAdmin();
  const { bulkPriceUpdateEnabled } = useSyncControls(authUser, userIsAdmin);
  const { paymentStatus, nextPaymentAmount, nextPaymentDueDate, isEmployee } = usePaymentStatus(authUser);
  const navigation = useMemo(
    () => getVisibleNavigation(userIsAdmin, { showPrices: bulkPriceUpdateEnabled === true }),
    [userIsAdmin, bulkPriceUpdateEnabled],
  );
  const activeKey = resolveActiveNavKey(location.pathname, userIsAdmin);
  const pageMeta = resolvePageMeta(location.pathname, userIsAdmin);
  const totalOpenDifferences = Number(summary?.totalOpen || 0);
  const unreadOrders = Number(orderSummary?.unreadCount || 0);

  useEffect(() => {
    const media = window.matchMedia(desktopMediaQuery);
    const syncViewport = (event) => {
      setIsDesktop(event.matches);
      if (event.matches) {
        setMobileOpen(false);
      }
    };

    syncViewport(media);
    media.addEventListener('change', syncViewport);

    return () => media.removeEventListener('change', syncViewport);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMobileOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  useEffect(() => {
    if (currentUser) return;
    navigate('/login', { replace: true });
  }, [currentUser, navigate]);

  const handleLogout = () => {
    userLogout();
    navigate('/login');
  };

  const handleNavigate = () => {
    if (!isDesktop) {
      setMobileOpen(false);
    }
  };

  const renderSidebar = () => (
    <aside
      className={`app-shell__sidebar${collapsed ? ' is-collapsed' : ''}${!isDesktop ? ' is-drawer' : ''}${mobileOpen ? ' is-open' : ''}`}
      aria-label="Navegación principal"
    >
      <div className="app-shell__sidebar-inner">
        <div className="app-shell__brand-row">
          <Link to="/inicio" className="app-shell__brand" onClick={handleNavigate}>
            <span className="app-shell__brand-mark">M</span>
            {!collapsed ? <span>MercuryCom</span> : null}
          </Link>
          {isDesktop ? (
            <button
              type="button"
              className="app-shell__icon-button"
              aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
              onClick={() => setCollapsed((prev) => !prev)}
            >
              <Icon name={collapsed ? 'angle double right' : 'angle double left'} style={{ margin: 0 }} />
            </button>
          ) : null}
        </div>

        <nav className="app-shell__nav">
          {navigation.map((group) => (
            <div key={group.key} className="app-shell__nav-group">
              {!collapsed ? <div className="app-shell__nav-group-title">{group.label}</div> : null}
              <div className="app-shell__nav-group-items">
                {group.items.map((item) => (
                  <SidebarNavItem
                    key={item.key}
                    item={item}
                    active={activeKey === item.key}
                    collapsed={collapsed}
                    badge={
                      item.badgeKey === 'totalOpen'
                        ? totalOpenDifferences
                        : item.badgeKey === 'unreadOrders'
                          ? unreadOrders
                          : 0
                    }
                    onNavigate={handleNavigate}
                    showChildren={item.key === 'canales' && activeKey === 'canales'}
                    pathname={location.pathname}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );

  return (
    <div className="app-shell">
      {isDesktop ? renderSidebar() : null}

      {!isDesktop && mobileOpen ? (
        <>
          <div className="app-shell__backdrop" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          {renderSidebar()}
        </>
      ) : null}

      <div className="app-shell__main">
        <header className="app-shell__topbar">
          <div className="app-shell__topbar-left">
            {!isDesktop ? (
              <button
                type="button"
                className="app-shell__icon-button"
                aria-label="Abrir navegación"
                onClick={() => setMobileOpen(true)}
              >
                <Icon name="bars" style={{ margin: 0 }} />
              </button>
            ) : null}
            <div>
              <div className="app-shell__page-title">{pageMeta.title}</div>
              {pageMeta.description ? <div className="app-shell__page-description">{pageMeta.description}</div> : null}
            </div>
          </div>

          <div className="app-shell__topbar-right">
            {totalOpenDifferences > 0 && activeKey !== 'diferencias' ? (
              <Button
                as={Link}
                to="/stock-inconsistencies"
                basic
                color="violet"
                size="small"
              >
                Alertas de stock
                <Label color="red" circular size="mini" style={{ marginLeft: 8 }}>
                  {totalOpenDifferences}
                </Label>
              </Button>
            ) : null}

            <Dropdown
              pointing="top right"
              icon={null}
              trigger={(
                <button type="button" className="app-shell__account-trigger">
                  <span className="app-shell__account-avatar">{String(currentUsername || 'U').charAt(0).toUpperCase()}</span>
                  <span className="app-shell__account-copy">
                    <strong>{currentUsername}</strong>
                    <span>{currentUserEmail}</span>
                  </span>
                  <Icon name="angle down" />
                </button>
              )}
            >
              <Dropdown.Menu>
                {userIsAdmin ? <Dropdown.Item as={Link} to="/users" text="Configuración" icon="settings" /> : null}
                <Dropdown.Item text="Cerrar sesión" icon="sign-out" onClick={handleLogout} />
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </header>

        <div className="app-shell__body">
          {['warning', 'critical', 'overdue'].includes(paymentStatus) ? (
            <PaymentNoticeBanner
              variant={paymentStatus}
              dueDate={nextPaymentDueDate}
              amount={nextPaymentAmount}
              employee={isEmployee}
            />
          ) : null}
          <StockInconsistencyBanner />
          <main className="app-shell__content">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}

export default AppShell;
